/**
 * The account pool, and the Account × Model matrix.
 *
 * Two rules shape this screen.
 *
 * First: nothing here is a credential. The wire type carries no token, no
 * cookie jar and no profile directory, and this file never asks for one. What
 * an operator can see is what Kimene *observed* about an account — its state,
 * its measured headroom, when it was last probed. Where the provider publishes
 * no headroom, the meter is hatched and the number is an em dash in the UNKNOWN
 * tone; it is never a full bar, because a full bar is a claim.
 *
 * Second: an UNCONFIGURED slot must look unconfigured. It is drawn dimmed, with
 * no meter, no confidence badge and no probe action — only the one action that
 * makes sense, which is to go and sign in. A slot that looks signed-in when it
 * is not is the failure mode this whole screen exists to prevent.
 *
 * Both rules have the same shape as the bug that took this screen down: the
 * matrix decoded a `cells` array the server has never sent, and every card drew
 * an `activeLease` that was `undefined` on all of them — so every account was
 * reported free, and being wrong looked exactly like being right. The types
 * below are written from the handlers in internal/api, field for field.
 */

import clsx from 'clsx'
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, createContext, useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { AddAccountDialog } from './AddAccountDialog'
import { SignInDialog } from './SignInDialog'
import {
  Page,
  PageHeader,
  Panel,
  Badge,
  Button,
  Meter,
  StatusDot,
  Skeleton,
  type Tone,
} from '@/components/primitives'
import {
  QueryState,
  Segmented,
  Notice,
  RowCheckbox,
  useApiErrorMessage,
} from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { type AccountUsage, type UsageWindow } from '@/components/AccountUsage'
import { useT, useLocale } from '@/i18n/context'
import { formatDateTime, formatPercent, formatRelative, formatDuration } from '@/i18n'
import type { ReactNode } from 'react'
import { GripVertical } from 'lucide-react'
import { Icon } from '@/components/icon'
import { accountStateTone, confidenceTone, availabilityTone, usageSeverityTone } from '@/lib/tone'
import { useManualOrder, useDragAnnouncements } from '@/hooks/useManualOrder'
import { useSelectionMode } from '@/hooks/useSelectionMode'
import type {
  AccountState,
  Confidence,
  ID,
  ISOTime,
  ModelAvailability,
  Provider,
} from '@/types/domain'
// The bulk bar is the fleet's pattern; its styles come from there so the two
// screens cannot drift into two different selection bars.
import './HostsPage.css'
import './AccountsPage.css'

/**
 * api.PageResponse. Declared here rather than imported from the API client,
 * whose copy has `nextCursor` required — the server omits it whenever there is
 * no further page, which is every page these pools currently return.
 */
interface Page<T> {
  items: T[]
  nextCursor?: string
  hasMore: boolean
}

/**
 * api.AccountProfileResponse — the stored profile as the wire redacts it.
 *
 * Not `AccountProfile & Meta` from types/domain, which is what this file used
 * to decode into: the handler builds this response by hand and it carries no
 * `createdBy`/`updatedBy`, so inheriting the domain envelope declared two
 * fields that are `undefined` at runtime. What it does carry is
 * `profileConfigured` — a boolean standing in for the credential root, which is
 * the whole of what the browser is ever told about it.
 */
interface AccountProfileResponse {
  id: ID
  identityId: ID
  provider: Provider
  label: string
  hostId: ID
  authMode?: string
  imported: boolean
  profileConfigured: boolean
  /** The credential directory is on storage every host mounts, so this account
   *  runs anywhere rather than only on `hostId` (ADR-0013). */
  sharedStorage: boolean
  state: AccountState
  enabled: boolean
  allowedModels?: ID[]
  preferredModels?: ID[]
  maxConcurrentTurns: number
  routingWeight: number
  lastProbeAt?: ISOTime
  lastRateLimitAt?: ISOTime
  resetAt?: ISOTime
  createdAt: ISOTime
  updatedAt: ISOTime
  version: number
}

/**
 * api.CapacityResponse — one observation of remaining headroom.
 *
 * `source` is omitempty and there is no `accountProfileId`: the observation is
 * already nested inside the account it belongs to. Both differ from the domain
 * snapshot this screen previously decoded into.
 */
interface CapacityResponse {
  remainingFraction?: number
  confidence: Confidence
  source?: string
  observedAt: ISOTime
  resetAt?: ISOTime
  windowLabel?: string
}

/** api.AccountIdentitySummary — the subscription's non-secret identity, read
 *  from the provider's own config. Host-independent; never a credential. */
interface AccountIdentitySummary {
  emailAddress?: string
  displayName?: string
  organizationName?: string
  organizationType?: string
  organizationRole?: string
  billingType?: string
  subscriptionType?: string
  rateLimitTier?: string
  accountCreatedAt?: ISOTime
  subscriptionCreatedAt?: ISOTime
  source?: string
}

/* AccountUsage / UsageWindow now live in components/AccountUsage.tsx (shared with
   the cockpit). AccountUsage is imported above. */

/** api.AccountOverview — what `/accounts` adds to the profile, resolved
 *  server-side so the browser makes no joins of its own. */
export interface AccountOverview extends AccountProfileResponse {
  identityLabel?: string
  hostName?: string
  capacity?: CapacityResponse
  activeSessions: number
  availableModels: number
  canAcceptWork: boolean
  /** The subscription's own identity + usage, host-independent. */
  identity?: AccountIdentitySummary
  usage?: AccountUsage
  /** Presence: which machines the account runs on and the projects it has most
   *  recently worked. Derived from execution history, not a foreign key. */
  hosts?: string[]
  recentProjects?: ProjectRef[]
}

/** api.ProjectRef — a project named for a link. */
interface ProjectRef {
  id: ID
  name: string
}

/** api.AccountMatrixResponse. The pairings are `entries`; this file read
 *  `cells`, which is not a field any handler sends — `data.cells is not
 *  iterable` is what took the screen down. */
interface AccountMatrixResponse {
  accounts: AccountProfileResponse[]
  models: MatrixModel[]
  entries: AccountMatrixEntry[]
}

/**
 * A matrix column head: domain.ModelDefinition as the matrix embeds it.
 *
 * Only the three fields the header renders are declared, and all three are
 * required in Go. The full definition — capability flags, context window,
 * discovery provenance — belongs to the models screen; a second copy here would
 * be another thing to keep in step with the Go struct for no reader's benefit.
 */
interface MatrixModel {
  id: ID
  provider: Provider
  displayName: string
  externalId: string
}

/** api.AccountMatrixEntry — one verified pairing. */
interface AccountMatrixEntry {
  accountProfileId: ID
  modelId: ID
  availability: ModelAvailability
  lastVerifiedAt?: ISOTime
  verificationSource?: string
  failureReason?: string
  preferred: boolean
  observedLatencyMs?: number
}

/** api.ProbeAccountResponse. The probe runs inside the request and answers with
 *  what it established, so this screen reports the verdict rather than claiming
 *  something was merely "started". */
interface ProbeAccountResponse {
  accountProfileId: ID
  hostId: ID
  state: AccountState
  verifiedModels: number
  unavailableModels: number
  unverifiedModels: number
  cliVersion?: string
  probedAt: ISOTime
  detail?: string
}

type ViewMode = 'cards' | 'table'

function capacityTone(fraction: number | null | undefined): Tone {
  if (fraction == null) return 'unknown'
  if (fraction < 0.15) return 'err'
  if (fraction < 0.35) return 'warn'
  return 'ok'
}

/** True when the slot has never been set up. Kept as a predicate rather than an
 *  inline comparison because three different renderings depend on it agreeing. */
function isUnconfigured(a: AccountOverview): boolean {
  return a.state === 'UNCONFIGURED'
}

/** Whether the account actually needs an interactive sign-in: never set up, or
 *  a credential that expired. A READY account does not — so the sign-in action is
 *  only offered when one of these is true, not on every card. */
function needsLogin(a: AccountOverview): boolean {
  return a.state === 'UNCONFIGURED' || a.state === 'AUTH_REQUIRED'
}

/** What the operator sees this account called: the subscription's own display
 *  name where it reports one, the profile's label otherwise.
 *
 *  One function rather than the expression written wherever it is needed,
 *  because the two had already drifted: the card headed itself "Arpanet" and
 *  the confirmation it opened asked about "arpanetbilisim@gmail.com". A dialog
 *  that names something other than the card it came from is, to the person
 *  reading it, a dialog about a different account. */
function accountName(a: AccountOverview): string {
  return a.identity?.displayName || a.label
}

/**
 * Opens the sign-in pane for a session, on this page.
 *
 * A context rather than threaded props: the three places that start a sign-in —
 * the card's action, the slot row's, and the add dialog — sit at three different
 * depths, and all three used to answer with the same `navigate('/live/…')`. One
 * dialog at the page level replaces all of it, and nothing in between has to
 * carry a prop it does not use.
 */
/** How many accounts a bulk probe checks at once. Four keeps the control host's
 *  CLI processes and the provider's concurrency both well inside anything either
 *  would notice, while turning a five-account sweep from a minute into one probe's
 *  wait. */
const probeConcurrency = 4

const OpenSignInPane = createContext<(sessionId: string, accountName: string) => void>(() => {})

function useOpenSignInPane() {
  return useContext(OpenSignInPane)
}

export function AccountsPage() {
  const t = useT()
  const [view, setView] = useState<ViewMode>(
    () => (localStorage.getItem('kimene.accounts.view') as ViewMode) || 'cards',
  )

  useEffect(() => {
    localStorage.setItem('kimene.accounts.view', view)
  }, [view])

  const [adding, setAdding] = useState(false)
  // The accounts a bulk action would act on. Both views: a card carries a selector
  // as well as a row does, and deciding otherwise once put the capability behind a
  // view switch nobody had a reason to press.
  const [selected, setSelected] = useState<string[]>([])

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<Page<AccountOverview>>('/accounts'),
  })

  /**
   * The operator's own order, over the server's.
   *
   * There is no column sort here, so the automatic order is whatever the API
   * returned — which is a fact about the database, not about what this operator
   * watches. Applied once, before the split below, so the cards and the table
   * agree; an account the order has never seen sorts after the ones it has.
   */
  const manual = useManualOrder('kimene.accounts.order')
  // The boxes are behind a toggle now, in the cards as well as the table.
  const clearChecked = useCallback(() => setSelected([]), [])
  const { selecting, toggle: toggleSelecting } = useSelectionMode(selected, clearChecked)
  const items = useMemo(
    () => manual.apply(accounts.data?.items ?? []),
    [accounts.data, manual],
  )
  // Configured accounts lead as full cards; never-set-up slots are collected into
  // a compact list below, so a pool that is mostly empty slots does not bury its
  // one working account under eight identical "not configured" cards.
  const configuredAccounts = items.filter((a) => !isUnconfigured(a))
  const slotAccounts = items.filter(isUnconfigured)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const nameOf = useCallback(
    (id: string) => items.find((a) => a.id === id)?.label ?? id,
    [items],
  )
  const announcements = useDragAnnouncements(nameOf)

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const from = String(e.active.id)
      const over = e.over ? String(e.over.id) : null
      if (!over || from === over) return
      const ids = items.map((a) => a.id)
      const a = ids.indexOf(from)
      const b = ids.indexOf(over)
      if (a < 0 || b < 0) return
      manual.set(arrayMove(ids, a, b))
    },
    [items, manual],
  )

  // Memoised: a new array each render makes dnd-kit re-register mid-gesture, and
  // the drag then ends where it began.
  const cardIds = useMemo(() => configuredAccounts.map((a) => a.id), [configuredAccounts])
  const rowIds = useMemo(() => items.map((a) => a.id), [items])

  /** The sign-in pane, shown over this page instead of on another one. */
  const [signInPane, setSignInPane] = useState<{ id: string; name: string } | null>(null)
  const openSignInPane = useCallback(
    (id: string, name: string) => setSignInPane({ id, name }),
    [],
  )

  return (
    <OpenSignInPane.Provider value={openSignInPane}>
    <Page
      header={
        <PageHeader
          title={t('account.title')}
          subtitle={t('account.subtitle')}
          actions={
            <>
              <Segmented
                value={view}
                onChange={setView}
                ariaLabel={t('common.view')}
                options={[
                  { value: 'cards', label: t('common.cards') },
                  { value: 'table', label: t('common.table') },
                ]}
              />
              {/* The permanent affordance for selection, beside the view switch,
                  now that the boxes are not. Same toggle recipe the fleet's
                  filters use — this screen already imports that stylesheet. */}
              <button
                type="button"
                className={clsx('k-fleet__toggle', selecting && 'k-fleet__toggle--on')}
                aria-pressed={selecting}
                onClick={toggleSelecting}
              >
                {t(selecting ? 'order.selectDone' : 'order.select')}
              </button>
              <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
                {t('account.add')}
              </Button>
            </>
          }
        />
      }
    >
      {adding && (
        <AddAccountDialog
          onClose={() => setAdding(false)}
          onSignIn={(sessionId, label) => {
            setAdding(false)
            openSignInPane(sessionId, label)
          }}
        />
      )}

      <QueryState
        pending={accounts.isPending}
        error={accounts.error}
        onRetry={() => void accounts.refetch()}
        empty={items.length === 0}
        emptyTitle={t('account.empty')}
        emptyHint={t('account.emptyHint')}
        skeleton={<Skeleton height={220} />}
      >
        {manual.order && (
          <button type="button" className="k-acct__reorder" onClick={manual.reset}>
            {t('order.manual')} · {t('order.reset')}
          </button>
        )}
        <DndContext sensors={sensors} onDragEnd={onDragEnd} accessibility={{ announcements }}>
        {view === 'cards' ? (
          <>
            {configuredAccounts.length > 0 && (
              <>
                {/* Bulk actions belong to whichever view the operator is in, and
                    this is the one they land on. A card can carry a selector as
                    well as a row can; deciding otherwise put the capability
                    behind a view switch nobody had a reason to press. */}
                {selecting && (
                  <AccountBulkBar
                    accounts={configuredAccounts.filter((a) => selected.includes(a.id))}
                    onClear={() => setSelected([])}
                  />
                )}
                <SortableContext items={cardIds} strategy={rectSortingStrategy}>
                  <div className="k-cardgrid k-acctgrid">
                    {configuredAccounts.map((a) => (
                      <AccountCard
                        key={a.id}
                        account={a}
                        selected={selected.includes(a.id)}
                        selecting={selecting}
                        onSelect={(on) =>
                          setSelected((prev) =>
                            on ? [...prev, a.id] : prev.filter((x) => x !== a.id),
                          )
                        }
                      />
                    ))}
                  </div>
                </SortableContext>
              </>
            )}
            {slotAccounts.length > 0 && (
              <Panel title={t('account.slotsTitle')} className="k-acct__slots">
                <ul className="k-slotlist">
                  {slotAccounts.map((a) => (
                    <AccountSlot key={a.id} account={a} />
                  ))}
                </ul>
              </Panel>
            )}
          </>
        ) : (
          <Panel flush>
            <>
              {/* Above the table and always present: the bar used to appear only
                  once a row was ticked, so nothing said the rows could be
                  ticked. */}
              {selecting && (
                <AccountBulkBar
                  accounts={items.filter((a) => selected.includes(a.id))}
                  onClear={() => setSelected([])}
                />
              )}
              <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                <AccountTable
                  accounts={items}
                  selected={selected}
                  selecting={selecting}
                  onSelect={setSelected}
                />
              </SortableContext>
            </>
          </Panel>
        )}
        </DndContext>
      </QueryState>

      <MatrixPanel />
    </Page>
    {signInPane && (
      <SignInDialog
        sessionId={signInPane.id}
        accountName={signInPane.name}
        onClose={() => setSignInPane(null)}
      />
    )}
    </OpenSignInPane.Provider>
  )
}

/* ------------------------------------------------------------ Account card */

/** tierShort turns "default_claude_max_20x" into "20x"; planLabel joins the
 *  subscription type with it into "max · 20x" for the one plan chip. */
function tierShort(tier?: string): string {
  return tier?.match(/(\d+x)$/)?.[1] ?? tier ?? ''
}
function planLabel(identity?: AccountIdentitySummary): string {
  if (!identity) return ''
  return [identity.subscriptionType, tierShort(identity.rateLimitTier)].filter(Boolean).join(' · ')
}

/** The human name for a usage window's kind, matching AccountUsagePanel. */
function windowName(t: ReturnType<typeof useT>, w: UsageWindow): string {
  switch (w.kind) {
    case 'session':
      return t('account.usageWindow.session')
    case 'weekly_all':
      return t('account.usageWindow.weekly')
    case 'monthly_all':
      return t('account.usageWindow.monthly')
    case 'weekly_scoped':
      return t('account.usageWindow.weeklyScoped')
    default:
      return w.kind
  }
}

/** The binding window is the most-utilised one — the constraint that actually
 *  decides how much headroom is left — with the rest collapsed after it, so a
 *  card's height does not swing with how many windows a plan happens to expose.
 *  (Active-first only breaks ties.) */
function splitWindows(windows?: UsageWindow[]): { hero?: UsageWindow; rest: UsageWindow[] } {
  const ws = [...(windows ?? [])].sort((a, b) => {
    if (a.utilization !== b.utilization) return b.utilization - a.utilization
    return Number(b.isActive) - Number(a.isActive)
  })
  return { hero: ws[0], rest: ws.slice(1) }
}

/**
 * One configured account, drawn as a subscription instrument.
 *
 * The layout is a fixed hierarchy so every card is the same shape regardless of
 * how much a plan reports: an identity header, then the *binding headroom* as the
 * card's hero (the one number that decides whether it can take work), a tight
 * reach strip, a muted presence/tuning line, and a footer of actions pinned to
 * the bottom edge — which is what makes a row of cards align instead of ragging.
 * Never-set-up slots do not reach here; they are the compact list below.
 */
/**
 * One row of the account table, with its drag grip.
 *
 * The grip goes in the selector cell rather than a column of its own, as it does
 * on the fleet table: the two are both row-level controls, and a new column would
 * change a table whose widths several rules pin by position.
 */
function AccountRow({
  id,
  unconfigured,
  label,
  selecting,
  checked,
  onCheck,
  children,
}: {
  id: string
  unconfigured: boolean
  label: string
  /** Whether the table is offering its rows for selection. */
  selecting: boolean
  checked: boolean
  onCheck: (on: boolean) => void
  children: ReactNode
}) {
  const t = useT()
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id })
  return (
    <tr
      ref={setNodeRef}
      className={clsx(
        unconfigured && 'k-acctrow--unconfigured',
        isDragging && 'k-acctrow--dragging',
      )}
      style={{
        transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
      }}
    >
      <td className="k-td--rowctl" onClick={(e) => e.stopPropagation()}>
        {/* One or the other, as on the other two screens. */}
        {selecting ? (
          <RowCheckbox
            checked={checked}
            onChange={onCheck}
            label={t('account.selectOne', { name: label })}
          />
        ) : (
          <span
            className="k-acct__grip"
            aria-label={t('order.move', { name: label })}
            title={t('order.move', { name: label })}
            {...attributes}
            {...listeners}
          >
            <Icon icon={GripVertical} size={13} />
          </span>
        )}
      </td>
      {children}
    </tr>
  )
}

function AccountCard({
  account,
  selected,
  selecting,
  onSelect,
}: {
  account: AccountOverview
  selected: boolean
  /** Whether the pool is offering its accounts for selection. */
  selecting: boolean
  onSelect: (on: boolean) => void
}) {
  const t = useT()
  const { locale } = useLocale()

  const cap = account.capacity
  const capUnknown = cap == null || cap.remainingFraction == null || cap.confidence === 'UNKNOWN'
  const { hero, rest } = splitWindows(account.usage?.windows)

  // Codex reports and frames usage as headroom REMAINING — its own /status shows
  // "100% left" — while Claude reports percent USED. Showing each in its provider's
  // own idiom stops a codex pro account, whose generous weekly limit sits near 100%
  // left, from reading as a broken "0%". The meter fill and the number follow the
  // idiom; the tone is always danger-by-used, so red still means "near the limit"
  // for both.
  const remaining = account.provider === 'codex'
  const shownPct = (util: number) => (remaining ? 100 - util : util)

  const name = accountName(account)
  const email = account.identity?.emailAddress
  const plan = planLabel(account.identity)
  // The org name the providers report is the derived "<email>'s Organization",
  // which just repeats the email a line above it — noise. Only the role (admin /
  // member) is a fact the email does not already carry, so that is all that stays.
  const orgRole = account.identity?.organizationRole

  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: account.id,
  })

  return (
    <div
      ref={setNodeRef}
      className={clsx('k-acct__slot', isDragging && 'k-acct__slot--dragging')}
      style={{
        // Inline rather than a transition: prefers-reduced-motion flattens every
        // transition-duration to 0.01ms, so a transition-driven drag jumps.
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
      }}
    >
    <Panel framed className="k-acct">
      <div className="k-acct__card">
        <header className="k-acct__top">
          <div className="k-acct__idline">
            {selecting ? (
              <RowCheckbox
                checked={selected}
                onChange={onSelect}
                label={t('account.selectOne', { name: account.label })}
              />
            ) : (
              <span
                className="k-acct__grip"
                aria-label={t('order.move', { name: account.label })}
                title={t('order.move', { name: account.label })}
                {...attributes}
                {...listeners}
              >
                <Icon icon={GripVertical} size={13} />
              </span>
            )}
            <StatusDot
              tone={accountStateTone(account.state)}
              live={account.state === 'BUSY'}
              label={account.state}
            />
            <span className="k-acct__name" title={name}>
              {name}
            </span>
            <span className="k-acct__tags">
              {plan && (
                <Badge tone="accent" subtle>
                  {plan}
                </Badge>
              )}
              <Badge tone="neutral" subtle mono>
                {account.provider}
              </Badge>
              {account.imported && (
                <Badge tone="info" subtle>
                  {t('account.imported')}
                </Badge>
              )}
              <Badge tone={accountStateTone(account.state)} subtle>
                {account.state}
              </Badge>
            </span>
          </div>
          {(email || orgRole) && (
            <div className="k-acct__subline">
              {email && (
                <span className="k-mono k-acct__email" title={email}>
                  {email}
                </span>
              )}
              {orgRole && <span className="k-acct__org">{orgRole}</span>}
            </div>
          )}
        </header>

        {account.state === 'AUTH_REQUIRED' && (
          /* The login is stale, not missing — the subscription is known but the
             CLI's stored auth no longer works. A distinct, actionable state, so a
             copper strip explains it; the sign-in control lives once in the footer. */
          <div className="k-acct__relogin" role="status">
            <span className="k-acct__relogin-text">{t('account.reloginHint')}</span>
          </div>
        )}

        {/* Headroom — the card's hero. The binding window's utilisation is the one
            figure that decides whether the account can take work, so it is the
            biggest thing on the card; the other windows collapse to one line. When
            the provider publishes nothing, the derived capacity stands in, hatched
            and violet when it too is unmeasured — never a full bar, which is a
            claim. */}
        <div className="k-acct__hr">
          {hero ? (
            <>
              <div className="k-acct__hr-top">
                <span className="k-acct__hr-name">
                  {windowName(t, hero)}
                  {hero.scopeModel && <span className="k-mono k-acct__hr-scope"> · {hero.scopeModel}</span>}
                </span>
                <span className="k-mono k-acct__hr-pct">
                  {Math.round(shownPct(hero.utilization))}%
                  {remaining && <span className="k-acct__hr-left"> {t('account.usageLeft')}</span>}
                </span>
              </div>
              <div className="k-acct__hr-meter">
                <Meter
                  value={Math.min(1, shownPct(hero.utilization) / 100)}
                  tone={usageSeverityTone(hero.severity, hero.utilization)}
                  ariaLabel={`${name} ${hero.kind}`}
                />
              </div>
              <div className="k-acct__hr-foot">
                <span
                  title={hero.resetAt ? formatDateTime(hero.resetAt, locale) : undefined}
                >
                  {hero.resetAt
                    ? t('account.usageResets', { when: formatRelative(hero.resetAt, locale) })
                    : ''}
                </span>
                {rest.length > 0 && (
                  <span className="k-acct__hr-rest">
                    {rest.map((w) => (
                      <span key={w.kind}>
                        {windowName(t, w)}{' '}
                        <span className="k-mono">{Math.round(shownPct(w.utilization))}%</span>
                      </span>
                    ))}
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="k-acct__hr-top">
                <span className="k-acct__hr-name">{t('account.capacity')}</span>
                <span
                  className={`k-mono k-acct__hr-pct k-acct__hr-pct--${capUnknown ? 'unknown' : 'known'}`}
                >
                  {capUnknown ? '—' : formatPercent(cap?.remainingFraction)}
                </span>
              </div>
              <div className="k-acct__hr-meter">
                <Meter
                  value={capUnknown ? null : (cap?.remainingFraction ?? null)}
                  tone={capacityTone(capUnknown ? null : cap?.remainingFraction)}
                  unknownLabel="?"
                  ariaLabel={`${name} ${t('account.capacity')}`}
                />
              </div>
              <div className="k-acct__hr-foot">
                <span>
                  {capUnknown
                    ? t('account.capacityUnknownHint')
                    : `${cap?.windowLabel ? `${cap.windowLabel} · ` : ''}${t('common.source')}: ${cap?.source || '—'}`}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Reach — three aligned mini-stats, value over label. */}
        <div className="k-acct__reach">
          <div className="k-acct__stat">
            <span className="k-acct__stat-val k-mono">{account.availableModels}</span>
            <span className="k-acct__stat-lab">{t('account.reachModels')}</span>
          </div>
          <div className="k-acct__stat">
            <span className="k-acct__stat-val k-mono">{account.activeSessions}</span>
            <span className="k-acct__stat-lab">{t('account.reachSessions')}</span>
          </div>
          <div className="k-acct__stat">
            <span
              className={`k-acct__stat-val k-acct__stat-val--${account.canAcceptWork ? 'ok' : 'no'}`}
            >
              {account.canAcceptWork ? t('common.yes') : t('common.no')}
            </span>
            <span className="k-acct__stat-lab">{t('account.canAcceptWork')}</span>
          </div>
        </div>

        <MetaLine account={account} />

        <footer className="k-acct__foot">
          <AccountActions account={account} />
        </footer>
      </div>
    </Panel>
    </div>
  )
}

/** Presence + tuning, one muted zone: the host(s) the account has run on and the
 *  projects it most recently touched (chips), then the low-emphasis knobs —
 *  last probe, routing weight, concurrency — on one line. A recent rate limit is
 *  the single fact here allowed a colour. Presence is derived from execution
 *  history, not ownership: an account belongs to no server. */
function MetaLine({ account }: { account: AccountOverview }) {
  const t = useT()
  const { locale } = useLocale()
  const hosts = account.hosts?.length ? account.hosts : account.hostName ? [account.hostName] : []
  const projects = account.recentProjects ?? []
  return (
    <div className="k-acct__meta">
      {(hosts.length > 0 || projects.length > 0 || account.sharedStorage) && (
        <div className="k-acct__meta-chips">
          {/* Reach, not presence: the chips beside it say where this account has
              actually run, which is history. This says where it *can* run, and
              it is the answer to the question the fleet screen keeps raising. */}
          {account.sharedStorage && (
            <span className="k-chip k-chip--reach">{t('account.everyHost')}</span>
          )}
          {hosts.map((h) => (
            <span key={h} className="k-mono k-chip">
              {h}
            </span>
          ))}
          {projects.map((p) => (
            <span key={p.id} className="k-mono k-chip k-chip--project" title={p.name}>
              {p.name}
            </span>
          ))}
        </div>
      )}
      <p className="k-acct__policy">
        <span title={account.lastProbeAt ? formatDateTime(account.lastProbeAt, locale) : undefined}>
          {t('account.lastProbe')}{' '}
          {account.lastProbeAt ? formatRelative(account.lastProbeAt, locale) : t('state.never')}
        </span>
        <span>
          {t('account.routingWeight')} {account.routingWeight.toFixed(2)}
        </span>
        <span>
          {t('account.concurrency')} {account.maxConcurrentTurns}
        </span>
        {account.lastRateLimitAt && (
          <span className="k-acct__ratelimit">
            {t('account.lastRateLimit')} {formatRelative(account.lastRateLimitAt, locale)}
          </span>
        )}
      </p>
    </div>
  )
}

/* ---------------------------------------------------------------- Actions */

function SignInLink({ account, compact }: { account: AccountOverview; compact?: boolean }) {
  const t = useT()
  const openSignInPane = useOpenSignInPane()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()
  const [error, setError] = useState<string | null>(null)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['accounts'] })
    void queryClient.invalidateQueries({ queryKey: ['accounts-matrix'] })
  }

  // An imported account carries its own credential; Kimene never signs in for it.
  const signIn = useMutation({
    mutationFn: () => api.post<{ id: string }>(`/accounts/${account.id}/login`),
    onSuccess: (session) => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] })
      openSignInPane(session.id, accountName(account))
    },
    onError: (err) => setError(describe(err).title),
  })

  // The step after signing in: a probe confirms the credential landed and, on an
  // unconfigured account, the server activates it — so this one click turns a
  // just-signed-in slot into a READY account instead of leaving it looking
  // unconfigured with only a "sign in" button.
  const activate = useMutation({
    mutationFn: () => api.post<{ state: string }>(`/accounts/${account.id}/probe`),
    onSuccess: (r) => {
      invalidate()
      if (!['READY', 'LOW_HEADROOM', 'BUSY'].includes(r.state)) {
        setError(t('account.activateNotReady'))
      }
    },
    onError: (err) => setError(describe(err).title),
  })

  if (account.imported) {
    return <p className="k-acct__signin-hint">{t('account.openLoginHint')}</p>
  }
  return (
    <div className="k-acct__signin">
      <div className="k-acct__buttons">
        <Button variant="primary" size="sm" busy={signIn.isPending} onClick={() => signIn.mutate()}>
          {t('account.signIn')}
        </Button>
        {/* Shown alongside sign-in because the two are a sequence: sign in in the
            pane, then activate here once done. */}
        <Button
          variant="default"
          size="sm"
          busy={activate.isPending}
          onClick={() => activate.mutate()}
        >
          {t('account.activate')}
        </Button>
      </div>
      {error && <Notice tone="err">{error}</Notice>}
      {/* The sign-in flow opens a terminal on the host and the operator completes
          it there. Saying so is the difference between trusting the console with
          a password and understanding that it never sees one. In the compact slot
          row the hint is dropped — the row has no space for it and the same words
          sit on the full card the slot becomes once it is signed in. */}
      {!compact && <p className="k-acct__signin-hint">{t('account.signInThenActivate')}</p>}
    </div>
  )
}

/** A never-set-up account as one compact row, not a full card: a status dot, its
 *  label and provider, and the sign-in actions on the trailing edge. */
function AccountSlot({ account }: { account: AccountOverview }) {
  return (
    <li className="k-slot">
      <StatusDot tone="neutral" label={account.state} />
      <span className="k-slot__label">{account.label}</span>
      <Badge tone="neutral" subtle mono>
        {account.provider}
      </Badge>
      <div className="k-slot__action">
        <SignInLink account={account} compact />
      </div>
    </li>
  )
}

function AccountActions({ account }: { account: AccountOverview }) {
  const t = useT()
  const openSignInPane = useOpenSignInPane()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()
  const confirm = useConfirm()
  const [message, setMessage] = useState<{
    tone: Tone
    text: string
    /** The provider's own words, kept apart from the translated summary and shown
     *  as the machine value it is — collapsed, mono — never spliced into the
     *  Turkish sentence. */
    detail?: string
  } | null>(null)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['accounts'] })
    void queryClient.invalidateQueries({ queryKey: ['accounts-matrix'] })
  }

  const probe = useMutation({
    mutationFn: () => api.post<ProbeAccountResponse>(`/accounts/${account.id}/probe`),
    // The probe runs inside the request, so the response is the answer. Saying
    // "probe started" and dropping the body — which is what this did — hid the
    // one thing the operator asked for, including the reason a probe that
    // reached nothing gives for reaching nothing.
    onSuccess: (result) => {
      // Tone follows the account's resolved state, not an ad-hoc count: a
      // DEGRADED account read red on its own chip but amber here, the same
      // condition wearing two colours. accountStateTone is the one source of
      // that mapping.
      setMessage({
        tone: accountStateTone(result.state),
        text: t('account.probeResult', {
          verified: result.verifiedModels,
          unavailable: result.unavailableModels,
          unverified: result.unverifiedModels,
        }),
        detail: result.detail,
      })
      invalidate()
    },
    onError: (err) => setMessage({ tone: 'err', text: describe(err).title }),
  })

  const toggle = useMutation({
    // The body is an AccountOverview and is deliberately ignored: the list
    // query is invalidated instead, so one row cannot disagree with the rest.
    mutationFn: () =>
      api.post<unknown>(`/accounts/${account.id}/enable`, {
        enabled: !account.enabled,
      }),
    onSuccess: invalidate,
    onError: (err) => setMessage({ tone: 'err', text: describe(err).title }),
  })

  const signIn = useMutation({
    mutationFn: () => api.post<{ id: string }>(`/accounts/${account.id}/login`),
    onSuccess: (session) => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] })
      openSignInPane(session.id, accountName(account))
    },
    onError: (err) => setMessage({ tone: 'err', text: describe(err).title }),
  })

  const del = useMutation({
    mutationFn: () => api.del<void>(`/accounts/${account.id}`),
    onSuccess: invalidate,
    // The backend refuses an enabled account or one with a live session (409);
    // its own message is the honest reason, surfaced as-is.
    onError: (err) => setMessage({ tone: 'err', text: describe(err).title }),
  })

  // Move the credential directory onto shared storage, which is what turns an
  // account signed in on one machine into one every host can use (ADR-0013).
  // The server does the copy and refuses what it cannot do safely — a live
  // session, an imported directory, a profile on another host — so this offers
  // it and reports the refusal rather than second-guessing it.
  const share = useMutation({
    mutationFn: () => api.post<AccountOverview>(`/accounts/${account.id}/share`),
    onSuccess: () => {
      invalidate()
      setMessage({ tone: 'ok', text: t('account.sharedNow') })
    },
    onError: (err) => setMessage({ tone: 'err', text: describe(err).title }),
  })

  return (
    <div className="k-acct__actions">
      {message &&
        (message.tone === 'err' ? (
          /* An error earns the boxed Notice — it is the one message that must not
             be missed. A routine probe verdict does not: it collapses to one quiet
             line so it never balloons the card the way the old green block did. */
          <Notice tone="err">
            {message.text}
            {message.detail && (
              <details className="k-notice__detail">
                <summary>{t('account.probeDetail')}</summary>
                <span className="k-mono">{message.detail}</span>
              </details>
            )}
          </Notice>
        ) : (
          <p className={`k-acct__result k-acct__result--${message.tone}`}>
            {message.text}
            {message.detail && (
              <details className="k-acct__result-detail">
                <summary>{t('account.probeDetail')}</summary>
                <span className="k-mono">{message.detail}</span>
              </details>
            )}
          </p>
        ))}
      <div className="k-acct__buttons">
        <Button size="sm" busy={probe.isPending} onClick={() => probe.mutate()}>
          {t('account.healthTest')}
        </Button>
        <Button size="sm" variant="default" busy={toggle.isPending} onClick={() => toggle.mutate()}>
          {account.enabled ? t('account.disable') : t('account.enable')}
        </Button>
        {/* Signing in for the first time, or after a credential expired: the
            account needs it, so the button is primary and asks nothing. Only an
            account Kimene owns can be signed in; an imported one carries its own
            credential and is left untouched. */}
        {!account.imported && needsLogin(account) && (
          <Button
            size="sm"
            variant="primary"
            busy={signIn.isPending}
            onClick={() => signIn.mutate()}
          >
            {t('account.signIn')}
          </Button>
        )}
        {/* And signing in *again*, on an account that is already READY.
            
            This was missing, and the gap had a shape: the console asked "is this
            signed in" and never "is it signed in as the right person". An
            account whose directory had been logged into with the wrong Google
            account sat at READY, reporting somebody else's e-mail and somebody
            else's quota, with no control anywhere that could change it — the
            fix was a shell on the control host. The same button, quiet rather
            than primary, because nothing here needs attention: it is a
            correction an operator comes looking for, not a state the card is
            reporting. It confirms first, since it replaces a credential that
            currently works. */}
        {!account.imported && !needsLogin(account) && (
          <Button
            size="sm"
            busy={signIn.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: t('account.reSignIn'),
                message: t('account.reSignInConfirm', { label: accountName(account) }),
                confirmLabel: t('account.reSignIn'),
                tone: 'default',
              })
              if (ok) signIn.mutate()
            }}
          >
            {t('account.reSignIn')}
          </Button>
        )}
        {/* Offered only where it can change something: an account Kimene owns,
            whose credentials are not already fleet-wide. The deployment may have
            no shared root at all, in which case the server says so — a button
            that explains the one setting standing between the operator and
            "every account on every server" beats no button and no explanation. */}
        {!account.imported && !account.sharedStorage && (
          <Button size="sm" busy={share.isPending} onClick={() => share.mutate()}>
            {t('account.share')}
          </Button>
        )}
        {/* Delete is destructive and version-fenced server-side; it refuses an
            enabled account or one with a live session. Offered only when disabled
            (else a tooltip says to disable first), and it confirms before firing. */}
        <Button
          size="sm"
          variant="danger"
          className="k-acct__delete"
          busy={del.isPending}
          disabled={account.enabled}
          title={account.enabled ? t('account.deleteDisableFirst') : undefined}
          onClick={async () => {
            const ok = await confirm({
              title: t('account.delete'),
              message: t('account.deleteConfirm', { label: accountName(account) }),
              confirmLabel: t('account.delete'),
            })
            if (ok) del.mutate()
          }}
        >
          {t('account.delete')}
        </Button>
      </div>
    </div>
  )
}

/* ----------------------------------------------------------- Account table */

/**
 * Probes the selected accounts, one at a time.
 *
 * The same shape as the fleet's bulk bar, and sequential for a related reason: a
 * probe spends a real request against the provider on the account's own quota,
 * so eight at once is eight simultaneous charges against a subscription an
 * operator is trying to conserve.
 */
export function AccountBulkBar({
  accounts,
  onClear,
}: {
  accounts: AccountOverview[]
  onClear: () => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState('')
  const [result, setResult] = useState<string | null>(null)
  const none = accounts.length === 0

  /**
   * Probes every selected account at once rather than one after another.
   *
   * A probe is ~90% the provider's own round trip and the CLI's startup, so the
   * fleet waited for the *sum* of them: measured at 10-25s each, five accounts
   * was most of a minute of a button saying "please wait". They are independent
   * — separate processes, separate credentials — so nothing was being ordered by
   * the loop except the operator's patience.
   *
   * Bounded, because each probe is a CLI process on the control host and an
   * unbounded fan-out over a large fleet would be a thundering herd against the
   * provider as much as against the box.
   */
  const probeAll = async () => {
    setRunning(true)
    setResult(null)
    // Never signed in: a probe would spend a request to rediscover that.
    const targets = accounts.filter((a) => !isUnconfigured(a))
    const skipped = accounts.length - targets.length
    let ok = 0
    let failed = 0
    let done = 0
    let detail = ''

    const queue = [...targets]
    const worker = async () => {
      for (;;) {
        const a = queue.shift()
        if (!a) return
        try {
          await api.post<ProbeAccountResponse>(`/accounts/${a.id}/probe`)
          ok++
        } catch (err) {
          failed++
          detail = describe(err).title
        }
        done++
        setProgress(t('fleet.bulkProgress', { done, total: targets.length, host: a.label }))
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(probeConcurrency, queue.length) }, worker),
    )

    void queryClient.invalidateQueries({ queryKey: ['accounts'] })
    void queryClient.invalidateQueries({ queryKey: ['accounts-matrix'] })
    setRunning(false)
    setProgress('')
    setResult(t('fleet.bulkDone', { ok, skipped, failed }) + (detail ? ` · ${detail}` : ''))
  }

  return (
    <div className="k-bulkbar" role="region" aria-label={t('fleet.bulkTitle')}>
      {/* Present whether or not anything is selected, saying what it wants. The
          capability existed and nothing on screen admitted to it. */}
      <span className={clsx('k-bulkbar__count', none && 'k-bulkbar__count--idle')}>
        {none ? t('account.bulkNone') : t('fleet.bulkSelected', { count: accounts.length })}
      </span>
      <Button size="sm" busy={running} disabled={none} onClick={() => void probeAll()}>
        {t('account.healthTest')}
      </Button>
      <Button size="sm" variant="ghost" disabled={running || none} onClick={onClear}>
        {t('fleet.bulkClear')}
      </Button>
      {progress && <span className="k-bulkbar__progress">{progress}</span>}
      {result && <span className="k-bulkbar__result">{result}</span>}
    </div>
  )
}

function AccountTable({
  accounts,
  selected,
  selecting,
  onSelect,
}: {
  accounts: AccountOverview[]
  selected: string[]
  /** Whether the table is offering its rows for selection. */
  selecting: boolean
  onSelect: (ids: string[]) => void
}) {
  const t = useT()
  const { locale } = useLocale()
  const allSelected = accounts.length > 0 && selected.length === accounts.length
  const someSelected = selected.length > 0 && !allSelected

  return (
    <div className="k-tablewrap">
      <table className="k-table k-table--responsive">
        <caption className="k-visually-hidden">{t('account.title')}</caption>
        <thead>
          <tr>
            {/* One cell for both row-level controls, the way the fleet table does
                it. Two columns would mean the selector's column appearing and
                disappearing with the mode, and a <thead> that disagrees with its
                <tbody> about how many columns there are shifts the whole table
                sideways. Hidden from a screen reader when empty: a column title
                read out on every row buys nothing. */}
            <th scope="col" className="k-td--rowctl" aria-hidden={!selecting}>
              {selecting && (
                <RowCheckbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => onSelect(allSelected ? [] : accounts.map((a) => a.id))}
                  label={t('fleet.selectAll')}
                />
              )}
            </th>
            <th scope="col">{t('account.label')}</th>
            <th scope="col">{t('account.provider')}</th>
            <th scope="col">{t('account.host')}</th>
            <th scope="col">{t('account.auth')}</th>
            <th scope="col">{t('account.capacity')}</th>
            <th scope="col">{t('account.models')}</th>
            <th scope="col">{t('account.canAcceptWork')}</th>
            <th scope="col">{t('account.activeSessions')}</th>
            <th scope="col">{t('account.lastRateLimit')}</th>
            <th scope="col">{t('account.lastProbe')}</th>
            <th scope="col">{t('account.concurrency')}</th>
            <th scope="col">{t('account.routingWeight')}</th>
            <th scope="col">{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => {
            const unconfigured = isUnconfigured(a)
            const cap = a.capacity
            const capUnknown =
              unconfigured ||
              cap == null ||
              cap.remainingFraction == null ||
              cap.confidence === 'UNKNOWN'
            // Chip agrees with the meter: UNKNOWN when there is no fraction.
            const capConfidence = capUnknown ? 'UNKNOWN' : (cap?.confidence ?? 'UNKNOWN')
            return (
              <AccountRow
                key={a.id}
                id={a.id}
                unconfigured={unconfigured}
                label={a.label}
                selecting={selecting}
                checked={selected.includes(a.id)}
                onCheck={(on) =>
                  onSelect(on ? [...selected, a.id] : selected.filter((x) => x !== a.id))
                }
              >
                <td className="k-td--strong" data-label={t('account.label')}>
                  <span className="k-inline">
                    <StatusDot tone={accountStateTone(a.state)} label={a.state} />
                    {a.label}
                  </span>
                  <span className="k-sub">{a.state}</span>
                </td>
                <td data-label={t('account.provider')}>
                  <Badge tone="neutral" subtle mono>
                    {a.provider}
                  </Badge>
                </td>
                <td className="k-mono" data-label={t('account.host')}>
                  {a.hostName ?? a.hostId.slice(0, 8)}
                </td>
                <td className="k-mono" data-label={t('account.auth')}>
                  {unconfigured ? (
                    <Badge tone="neutral" subtle>
                      {t('account.notConfigured')}
                    </Badge>
                  ) : (
                    (a.authMode ?? t('state.unknown'))
                  )}
                </td>
                <td data-label={t('account.capacity')}>
                  <div className="k-meterpair">
                    <Meter
                      value={capUnknown ? null : (cap?.remainingFraction ?? null)}
                      tone={capacityTone(capUnknown ? null : cap?.remainingFraction)}
                      unknownLabel="?"
                      ariaLabel={`${a.label} ${t('account.capacity')}`}
                    />
                    <span className={`k-mono k-acct__capval--${capUnknown ? 'unknown' : 'known'}`}>
                      {capUnknown ? '—' : formatPercent(cap?.remainingFraction)}
                    </span>
                  </div>
                  <Badge tone={confidenceTone(capConfidence)} subtle>
                    {t(`confidence.${capConfidence}`)}
                  </Badge>
                </td>
                <td className="k-mono k-td--num" data-label={t('account.models')}>
                  {a.availableModels}
                </td>
                <td data-label={t('account.canAcceptWork')}>
                  <Badge tone={a.canAcceptWork ? 'ok' : 'neutral'} subtle>
                    {a.canAcceptWork ? t('common.yes') : t('common.no')}
                  </Badge>
                </td>
                <td className="k-mono k-td--num" data-label={t('account.activeSessions')}>
                  {a.activeSessions}
                </td>
                <td data-label={t('account.lastRateLimit')}>
                  {a.lastRateLimitAt ? (
                    <span className="k-acct__ratelimit">
                      {formatRelative(a.lastRateLimitAt, locale)}
                    </span>
                  ) : (
                    <span className="k-dash">—</span>
                  )}
                </td>
                <td data-label={t('account.lastProbe')}>
                  {a.lastProbeAt ? (
                    formatRelative(a.lastProbeAt, locale)
                  ) : (
                    <Badge tone="unknown" subtle>
                      {t('state.never')}
                    </Badge>
                  )}
                </td>
                <td className="k-mono k-td--num" data-label={t('account.concurrency')}>
                  {a.maxConcurrentTurns}
                </td>
                <td className="k-mono k-td--num" data-label={t('account.routingWeight')}>
                  {a.routingWeight.toFixed(2)}
                </td>
                <td data-label={t('common.actions')}>
                  {unconfigured ? <SignInLink account={a} /> : <AccountActions account={a} />}
                </td>
              </AccountRow>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ------------------------------------------------------------- The matrix */

interface CellSelection {
  entry: AccountMatrixEntry | null
  /** The account/model of the clicked cell, kept independently of `entry` so a
   *  cell with no stored availability row (entry === null) still tracks which
   *  cell is open — otherwise its aria-expanded could never be true. */
  accountId: ID
  modelId: ID
  accountLabel: string
  modelName: string
  /** The cell the card belongs to.
   *
   *  The card used to store the click's coordinates and close on any scroll,
   *  because a fixed position computed once goes stale the moment the page
   *  moves. That made clicking a partly-visible cell impossible: the browser
   *  scrolls it into view, the scroll listener fires, and the card closes in the
   *  same frame it opened. Keeping the element instead lets the card follow it. */
  cell: HTMLElement
}

function MatrixPanel() {
  const t = useT()

  const matrix = useQuery({
    queryKey: ['accounts-matrix'],
    queryFn: () => api.get<AccountMatrixResponse>('/accounts/matrix'),
  })

  const data = matrix.data
  // A response that omits either collection must render the empty state, not
  // crash the whole console. Optional chaining rather than a cast: the server
  // is entitled to send a partial document, and a white screen is a far worse
  // answer than "nothing to show".
  const empty = !data?.accounts?.length || !data?.models?.length

  return (
    <Panel
      className="k-matrixpanel"
      title={t('account.matrix')}
      actions={
        <Button variant="ghost" size="sm" onClick={() => void matrix.refetch()}>
          {t('action.refresh')}
        </Button>
      }
      flush
    >
      <div className="k-matrixpanel__intro">
        <p className="k-matrixpanel__hint">{t('account.matrixHint')}</p>
        <MatrixLegend />
      </div>

      <QueryState
        pending={matrix.isPending}
        error={matrix.error}
        onRetry={() => void matrix.refetch()}
        empty={empty}
        emptyTitle={t('account.matrixEmpty')}
        emptyHint={t('account.matrixEmptyHint')}
        skeleton={
          <div className="k-matrixpanel__loading">
            <Skeleton height={220} />
          </div>
        }
      >
        {data && <MatrixGrid data={data} />}
      </QueryState>
    </Panel>
  )
}

const AVAILABILITY_GLYPH: Record<ModelAvailability, string> = {
  AVAILABLE: '●',
  DEGRADED: '◐',
  UNVERIFIED: '?',
  UNAVAILABLE: '✕',
  BLOCKED: '⊘',
}

function MatrixLegend() {
  const t = useT()
  const entries: ModelAvailability[] = [
    'AVAILABLE',
    'DEGRADED',
    'UNVERIFIED',
    'UNAVAILABLE',
    'BLOCKED',
  ]
  return (
    <ul className="k-legend">
      {entries.map((a) => (
        <li key={a} className="k-legend__item">
          <span className={`k-cellmark k-cellmark--${availabilityTone(a)}`} aria-hidden>
            {AVAILABILITY_GLYPH[a]}
          </span>
          {t(`account.availability.${a}`)}
        </li>
      ))}
      <li className="k-legend__item">
        <span className="k-cellmark k-cellmark--ok k-cellmark--preferred" aria-hidden>
          ●
        </span>
        {t('account.preferred')}
      </li>
    </ul>
  )
}

/**
 * The grid itself.
 *
 * Sticky in both axes, because the two questions an operator asks of a matrix
 * are "what can *this account* reach" and "who can reach *this model*", and
 * losing either label makes the other unanswerable. The whole thing scrolls
 * inside its own box: the page itself never scrolls sideways.
 */
function MatrixGrid({ data }: { data: AccountMatrixResponse }) {
  const t = useT()
  const [selection, setSelection] = useState<CellSelection | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  const accounts = data.accounts ?? []
  const models = data.models ?? []

  // `entries`, not `cells`. Defaulted because a matrix with no verified pairing
  // yet is the ordinary state of a fresh install — the grid must still draw,
  // with every cell UNVERIFIED, rather than fail on an absent array.
  const index = useMemo(() => {
    const map = new Map<string, AccountMatrixEntry>()
    for (const e of data.entries ?? []) map.set(`${e.accountProfileId}:${e.modelId}`, e)
    return map
  }, [data.entries])

  const close = useCallback(() => setSelection(null), [])

  useEffect(() => {
    if (!selection) return
    // Escape closes; scrolling no longer does. The card tracks its cell instead,
    // so a page that moves under it stays correct rather than dismissing the
    // thing the operator just asked for.
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onEscape)
    return () => window.removeEventListener('keydown', onEscape)
  }, [selection, close])

  /** Arrow keys move between cells. With thirty models across, tabbing through
   *  every cell to reach the last column is not a usable keyboard path. */
  const onGridKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const deltas: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    }
    const delta = deltas[e.key]
    if (!delta) return
    const active = document.activeElement as HTMLElement | null
    if (!active?.dataset.r || !active.dataset.c) return
    e.preventDefault()
    const r = Number(active.dataset.r) + delta[0]
    const c = Number(active.dataset.c) + delta[1]
    gridRef.current?.querySelector<HTMLElement>(`[data-r="${r}"][data-c="${c}"]`)?.focus()
  }

  return (
    <div className="k-matrix__outer">
      <div className="k-matrix__scroll" ref={gridRef} onKeyDown={onGridKeyDown}>
        <table className="k-matrix">
          <caption className="k-visually-hidden">{t('account.matrix')}</caption>
          <thead>
            <tr>
              <th className="k-matrix__corner" scope="col">
                <span className="k-visually-hidden">{t('account.label')}</span>
              </th>
              {models.map((m) => (
                <th key={m.id} scope="col" className="k-matrix__colhead" title={m.externalId}>
                  <span className="k-matrix__colhead-inner">{m.displayName}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accounts.map((a, r) => (
              <tr key={a.id}>
                <th scope="row" className="k-matrix__rowhead">
                  <span className="k-inline">
                    <StatusDot tone={accountStateTone(a.state)} label={a.state} />
                    <span className="k-matrix__rowname">{a.label}</span>
                  </span>
                  <span className="k-sub k-mono">{a.provider}</span>
                </th>
                {models.map((m, c) => {
                  // A model belongs to one provider; an account only ever probes
                  // its own provider's models. A claude account × a codex model is
                  // a structurally impossible pairing that will never have a
                  // record — mark it not-applicable rather than UNVERIFIED, which
                  // means "not checked yet" and dilutes the real signal.
                  if (m.provider !== a.provider) {
                    return (
                      <td key={m.id} className="k-matrix__cell">
                        <span
                          className="k-cellmark k-cellmark--na"
                          aria-label={`${a.label} × ${m.displayName}: ${t('account.availability.NA')}`}
                          title={t('account.availability.NA')}
                        >
                          ·
                        </span>
                      </td>
                    )
                  }
                  const entry = index.get(`${a.id}:${m.id}`) ?? null
                  const availability = entry?.availability ?? 'UNVERIFIED'
                  const tone = availabilityTone(availability)
                  const label = `${a.label} × ${m.displayName}: ${t(`account.availability.${availability}`)}`
                  return (
                    <td key={m.id} className="k-matrix__cell">
                      <button
                        type="button"
                        data-r={r}
                        data-c={c}
                        className={`k-cellmark k-cellmark--${tone} ${
                          entry?.preferred ? 'k-cellmark--preferred' : ''
                        } ${entry == null ? 'k-cellmark--norecord' : ''}`}
                        aria-label={label}
                        aria-haspopup="dialog"
                        aria-expanded={selection?.accountId === a.id && selection?.modelId === m.id}
                        onClick={(e) => {
                          setSelection({
                            entry,
                            accountId: a.id,
                            modelId: m.id,
                            accountLabel: a.label,
                            modelName: m.displayName,
                            cell: e.currentTarget,
                          })
                        }}
                      >
                        {AVAILABILITY_GLYPH[availability]}
                      </button>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selection && <CellPopover selection={selection} onClose={close} />}
    </div>
  )
}

/** The cell detail. Answers exactly the four questions a cell cannot: when it
 *  was last checked, by what, how fast it responded and — when it failed — why. */
function CellPopover({ selection, onClose }: { selection: CellSelection; onClose: () => void }) {
  const t = useT()
  const { locale } = useLocale()
  const ref = useRef<HTMLDivElement>(null)
  const entry = selection.entry

  const [rect, setRect] = useState(() => selection.cell.getBoundingClientRect())

  useEffect(() => {
    // preventScroll matters here. Focusing scrolls the element into view, and
    // the card's position is derived from where the cell is — so without it the
    // card would be placed, then the page would move beneath it.
    ref.current?.focus({ preventScroll: true })
  }, [])

  useEffect(() => {
    const cell = selection.cell
    // Follow the cell. Scroll fires far more often than a frame can be painted,
    // so the read is coalesced into one rAF rather than run per event —
    // getBoundingClientRect forces layout, and doing that per scroll event on a
    // grid this size is visible as jank.
    let frame = 0
    const track = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        setRect(cell.getBoundingClientRect())
      })
    }
    window.addEventListener('scroll', track, true)
    window.addEventListener('resize', track)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', track, true)
      window.removeEventListener('resize', track)
    }
  }, [selection.cell])

  const availability = entry?.availability ?? 'UNVERIFIED'
  const width = 268
  const left = Math.min(
    Math.max(8, rect.left + rect.width / 2 - width / 2),
    window.innerWidth - width - 8,
  )

  return (
    <>
      {/* The scrim does what an invisible document-level mousedown listener used
          to: a click anywhere else dismisses the card. Making it an element
          rather than a listener is what makes the behaviour true in the DOM —
          the controls underneath are genuinely not clickable while the card is
          open, which is what `aria-modal` asserts and what stops a keyboard or
          pointer user activating something they cannot see behind it. */}
      <div className="k-cellpop__scrim" onMouseDown={onClose} aria-hidden="true" />
      <div
        className="k-cellpop"
        role="dialog"
        aria-modal="true"
        aria-label={`${selection.accountLabel} × ${selection.modelName}`}
        tabIndex={-1}
        ref={ref}
        onKeyDown={(e) => {
          // aria-modal asserts focus is trapped here; make that true. Escape
          // closes; Tab cycles within the card (or stays on it when it holds no
          // focusable controls) rather than leaking to the scrimmed background.
          if (e.key === 'Escape') {
            onClose()
            return
          }
          if (e.key !== 'Tab') return
          const focusables = ref.current?.querySelectorAll<HTMLElement>(
            'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])',
          )
          if (!focusables || focusables.length === 0) {
            e.preventDefault()
            return
          }
          const first = focusables[0]
          const last = focusables[focusables.length - 1]
          if (!first || !last) return
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault()
            last.focus()
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault()
            first.focus()
          }
        }}
        style={{
          left,
          top: Math.min(rect.bottom + 6, window.innerHeight - 220),
          width,
        }}
      >
        <header className="k-cellpop__head">
          <span className="k-cellpop__who">
            {selection.accountLabel}
            <span className="k-sub k-mono">{selection.modelName}</span>
          </span>
          <Badge tone={availabilityTone(availability)} subtle>
            {t(`account.availability.${availability}`)}
          </Badge>
        </header>

        <dl className="k-cellpop__facts">
          <div>
            <dt>{t('model.lastVerified')}</dt>
            <dd>
              {entry?.lastVerifiedAt ? (
                <span title={formatDateTime(entry.lastVerifiedAt, locale)}>
                  {formatRelative(entry.lastVerifiedAt, locale)}
                </span>
              ) : (
                <Badge tone="unknown" subtle>
                  {t('account.neverVerified')}
                </Badge>
              )}
            </dd>
          </div>
          <div>
            <dt>{t('model.verificationSource')}</dt>
            <dd className="k-mono">
              {entry?.verificationSource || <span className="k-dash">—</span>}
            </dd>
          </div>
          <div>
            <dt>{t('common.latency')}</dt>
            <dd className="k-mono">
              {entry?.observedLatencyMs != null ? (
                formatDuration(entry.observedLatencyMs)
              ) : (
                <span className="k-dash">—</span>
              )}
            </dd>
          </div>
          {entry?.preferred && (
            <div>
              <dt>{t('account.preferred')}</dt>
              <dd>
                <Badge tone="accent" subtle>
                  {t('common.yes')}
                </Badge>
              </dd>
            </div>
          )}
        </dl>

        {entry?.failureReason && (
          <p className="k-cellpop__reason">
            <span className="k-label">{t('common.reason')}</span>
            {entry.failureReason}
          </p>
        )}

        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('action.close')}
        </Button>
      </div>
    </>
  )
}
